import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const PreAwardServicesPage = () => {
  const technicalScope = [
    'Review of FEED, tender packages, and project specifications',
    'Identification of scope gaps, ambiguities, and misalignments with industry standards',
    'Cross discipline interface checks (process–mechanical, mechanical–piping, electrical–instrumentation, civil–structural)',
    'Recommendations to optimize design intent and reduce downstream rework',
    'Verification of licensor requirements and process guarantees'
  ];

  const feedSupport = [
    'Conceptual design and preliminary engineering for upstream, midstream, and downstream facilities',
    'Process simulations, heat & material balances, and equipment sizing',
    'Preliminary PFDs, P&IDs, and layout development',
    'Constructability and operability assessments',
    'Environmental and regulatory compliance checks'
  ];

  const tenderEngineering = [
    'Preparation of technical proposals, execution methodologies, and engineering narratives',
    'Development of compliance matrices and deviation lists',
    'Engineering support for pre bid meetings, site visits, and technical clarifications',
    'Identification of long lead equipment and early vendor engagement',
    'Preparation of discipline-wise deliverable lists and engineering man-hour estimates'
  ];

  const costEstimation = [
    'Discipline-wise material take-offs (piping, mechanical, electrical, instrumentation, civil)',
    'BOQ preparation aligned with tender specifications',
    'Cost estimation using historical data, vendor quotes, and market benchmarks',
    'Identification of cost risks and contingency recommendations'
  ];

  const vendorQualification = [
    'Preparation of vendor pre qualification packages',
    'Technical evaluation of vendor proposals for rotating equipment, static equipment, instrumentation, and electrical systems',
    'Review of vendor data sheets, compliance statements, and technical deviations',
    'Coordination with OEMs for clarifications and early design alignment'
  ];

  const riskAssessment = [
    'Early-stage risk identification (technical, schedule, HSE, regulatory, supply chain)',
    'Development of mitigation strategies and risk registers',
    'Preliminary project schedules (Level 1/2) and resource planning',
    'Interface and dependency mapping for multi contract environments',
    'Logistics and constructability risk evaluation for remote or offshore sites'
  ];

  const whyItMatters = [
    'Clear and accurate scope definition',
    'Competitive and realistic cost estimates',
    'Reduced technical and commercial risks',
    'Stronger negotiation and bid positioning',
    'Faster mobilization after award',
    'Improved alignment with client expectations and industry standards'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-80 flex items-center justify-center overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Pre-Award Services
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Establishing the technical, commercial, and execution foundation for competitive bids
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Pre-Award Services for <span className="text-red-600">Oil & Gas Engineering Projects</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mb-8" />
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Pre award engineering establishes the technical, commercial, and execution foundation required to secure projects 
              in the oil & gas sector. By combining discipline engineering, cost intelligence, constructability insights, and risk 
              evaluation, these services help clients submit competitive bids, reduce uncertainties, and accelerate post award mobilization.
            </p>
          </div>

          {/* Technical Scope Definition */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Technical Scope Definition and Validation</h3>
            <p className="text-slate-700 mb-6">
              A clear and validated scope is essential in oil & gas projects where interfaces, regulatory requirements, and process 
              safety considerations are complex.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {technicalScope.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* FEED Support */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Concept and FEED-Level Engineering Support</h3>
            <p className="text-slate-700 mb-6">
              Early engineering ensures technical feasibility and alignment with client expectations.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {feedSupport.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Tender Engineering */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Tender Engineering and Proposal Development</h3>
            <p className="text-slate-700 mb-6">
              Oil & gas bids require detailed technical justification and alignment with client tender requirements.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {tenderEngineering.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Cost Estimation */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Cost Estimation and Material Take-Offs</h3>
            <p className="text-slate-700 mb-6">
              Accurate cost forecasting is critical in a sector where equipment, materials, and logistics drive major cost components.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {costEstimation.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Vendor Pre Qualification */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Vendor Pre Qualification and Technical Evaluation</h3>
            <p className="text-slate-700 mb-6">
              Oil & gas projects rely heavily on specialized equipment and certified vendors.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {vendorQualification.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Risk Assessment */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Risk Assessment and Project Planning</h3>
            <p className="text-slate-700 mb-6">
              Pre award risk identification helps avoid delays, cost overruns, and technical disputes.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {riskAssessment.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Client Engagement */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Client and Stakeholder Engagement</h3>
            <p className="text-slate-700 mb-6">
              Effective communication during the pre award phase strengthens client confidence.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-base">Participation in pre bid meetings, workshops, and technical discussions</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-base">Support in responding to client queries and clarifications</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-base">Alignment of engineering deliverables with client expectations and project objectives</span>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-base">Preparation of presentations and technical justifications for management reviews</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold text-slate-900 mb-6 text-center">Why Pre-Award Services Matter in Oil & Gas</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-8 text-center">
              Oil & gas projects involve high capital investment, strict regulatory frameworks, and complex technical interfaces. 
              Strong pre award engineering ensures:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {whyItMatters.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PreAwardServicesPage;